/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";

export enum EtriggerEventBillingRule {
  SUBSCRIPTION_EXPIRING = 'SUBSCRIPTION_EXPIRING',
  ACCOUNT_PASSWORD_UPDATE = 'ACCOUNT_PASSWORD_UPDATE',
  TRANSLATE_ACCOUNT = 'TRANSLATE_ACCOUNT',
}

export const TRIGGER_EVENT_BILLING_RULE_LABEL: Record<EtriggerEventBillingRule, string> = {
  [EtriggerEventBillingRule.SUBSCRIPTION_EXPIRING]: "Vencimiento de suscripción",
  [EtriggerEventBillingRule.ACCOUNT_PASSWORD_UPDATE]: "Actualización de contraseña de cuenta",
  [EtriggerEventBillingRule.TRANSLATE_ACCOUNT]: "Traspaso de cuenta",
};

export const TRIGGER_EVENT_BILLING_RULE_DESCRIPTION: Record<EtriggerEventBillingRule, string> = {
  [EtriggerEventBillingRule.SUBSCRIPTION_EXPIRING]:
    "Se dispara en relación a la fecha de vencimiento de una suscripción del cliente (usa 'Días relativos al vencimiento').",
  [EtriggerEventBillingRule.ACCOUNT_PASSWORD_UPDATE]:
    "Se dispara cuando se actualiza la contraseña de una cuenta de streaming.",
  [EtriggerEventBillingRule.TRANSLATE_ACCOUNT]:
    "Se dispara cuando se realiza un traspaso/transferencia de cuenta a otro cliente.",
};


export interface BillingRule {
  id: number;
  templateId: number;
  name: string;
  daysOffset: number;
  triggerEvent: EtriggerEventBillingRule ;
  sendTime: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreateBillingRuleRequest {
  templateId: number;
  name: string;
  daysOffset: number;
  triggerEvent: EtriggerEventBillingRule ;
  sendTime: string;
  isActive?: boolean;
}

export interface UpdateBillingRuleRequest {
  templateId?: number;
  name?: string;
  daysOffset?: number;
  triggerEvent?: string;
  sendTime?: string;
  isActive?: boolean;
}

export interface BillingRuleFilters {
  triggerEvent?: string;
  templateId?: number;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

export const billingRuleService = {
  async create(
    data: CreateBillingRuleRequest,
  ): Promise<IresponseData<BillingRule>> {
    try {
      const response = await apiClient.post<IresponseData<BillingRule>>(
        "/billing-rules",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al crear la regla de cobro. Por favor, intenta de nuevo.",
      );
    }
  },

  async getAll(
    filters?: BillingRuleFilters,
  ): Promise<IresponseDataPaginated<BillingRule[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<BillingRule[]>
      >("/billing-rules", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener las reglas de cobro. Por favor, intenta de nuevo.",
      );
    }
  },

  async getOne(id: number): Promise<IresponseData<BillingRule>> {
    try {
      const response = await apiClient.get<IresponseData<BillingRule>>(
        `/billing-rules/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener la regla de cobro. Por favor, intenta de nuevo.",
      );
    }
  },

  async update(
    id: number,
    data: UpdateBillingRuleRequest,
  ): Promise<IresponseData<BillingRule>> {
    try {
      const response = await apiClient.put<IresponseData<BillingRule>>(
        `/billing-rules/${id}`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar la regla de cobro. Por favor, intenta de nuevo.",
      );
    }
  },

  async remove(id: number): Promise<IresponseData<null>> {
    try {
      const response = await apiClient.delete<IresponseData<null>>(
        `/billing-rules/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al eliminar la regla de cobro. Por favor, intenta de nuevo.",
      );
    }
  },

  async toggle(id: number): Promise<IresponseData<BillingRule>> {
    try {
      const response = await apiClient.patch<IresponseData<BillingRule>>(
        `/billing-rules/${id}/toggle`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al cambiar el estado de la regla. Por favor, intenta de nuevo.",
      );
    }
  },
};
